import React, { useEffect, useState, useCallback, useMemo, memo, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Plus } from 'lucide-react';
import { addService, removeService, toggleService } from '../../api/services';
import ToggleButton from '../common/button/ToggleButton';
import ServiceLogo from '../common/logo/ServiceLogo';
import PillButton from '../common/button/PillButton';
import TooltipPortal from '../common/tooltip/TooltipPortal';

import styles from './styles/ServiceCard.module.css';

const cardVariants = {
    hidden: { opacity: 0, y: 14, scale: 0.985 },
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: 'spring', stiffness: 260, damping: 32, mass: 0.9 },
    },
    exit: { opacity: 0, y: 6, scale: 0.985, transition: { duration: 0.22 } },
};

const ServiceCard = forwardRef(({ service, setServices, onClick, type, isShowDelete = true }, ref) => {
    const [isEnabled, setIsEnabled] = useState(service.isEnabled);
    const [isLoading, setIsLoading] = useState(false);
    const [hoveredWarning, setHoveredWarning] = useState(false);
    const serviceId = useMemo(() => service.id, [service]);

    useEffect(() => {
        setIsEnabled(service.isEnabled);
    }, [service]);

    const handleToggle = useCallback(async () => {
        setIsLoading(true);
        try {
            await toggleService(serviceId);
            setIsEnabled(!isEnabled);
            setServices(prev => prev.map(s => 
                s.id === serviceId 
                    ? { ...s, isEnabled: !isEnabled }
                    : s
            ));
        } catch (error) {
            console.error('Error toggling service:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isEnabled, setServices]);

    const handleAdd = useCallback(async () => {
        setIsLoading(true);
        try {
            await addService(serviceId);
            setServices(prev => prev.map(s => 
                s.id === serviceId 
                    ? { ...s, isAdded: true, isEnabled: true }
                    : s
            ));
        } catch (error) {
            console.error('Error adding service:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, setServices]);

    const handleRemove = useCallback(async () => {
        if (!window.confirm('Are you sure you want to remove this service?')) return;

        setIsLoading(true);
        try {
            await removeService(serviceId);
            if (type === 'discover') {
                setServices(prev => prev.map(s => 
                    s.id === serviceId 
                        ? { ...s, isAdded: false, isEnabled: false }
                        : s
                ));
            } else if (type === 'added') {
                setServices(prev => prev.filter(s => s.id !== serviceId));
            }
        } catch (error) {
            console.error('Error removing service:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, setServices, type]);

    const needsAuthSetup = service?.requiresAuth && !service?.hasUserToken;
    const needsSecretsSetup = (service?.missingUserSecretsCount || 0) > 0;
    const hasWarning = needsAuthSetup || needsSecretsSetup;

    const tooltipText = useMemo(() => {
        const parts = [];
        if (needsAuthSetup) parts.push('OAuth required');
        if (needsSecretsSetup) parts.push('Missing secrets');
        return parts.join(' • ');
    }, [needsAuthSetup, needsSecretsSetup]);

    return (
        <motion.div
            ref={ref}
            className={`${styles.card} ${isLoading ? styles.loading : ''}`}
            variants={cardVariants}
            whileHover={{ y: -1, scale: 1.005 }}
            whileTap={{ scale: 0.995 }}
            layout
            transition={{ layout: { type: 'spring', stiffness: 260, damping: 34 } }}
        >
            <div className={styles.mainContent}>
                <div className={styles.header}>
                    <ServiceLogo
                        logo={service.logo}
                        name={service.name}
                        size="medium"
                    />
                    <div className={styles.titleBlock}>
                        <h3 className={styles.title} onClick={onClick}>{service.name}</h3>
                        <div className={styles.actions}>
                            {hasWarning && service?.isAdded && (
                                <TooltipPortal tooltip={tooltipText} show={hoveredWarning}>
                                    <div
                                        className={styles.warningDotContainer}
                                        onClick={e => { e.stopPropagation(); onClick(); }}
                                        onMouseEnter={() => setHoveredWarning(true)}
                                        onMouseLeave={() => setHoveredWarning(false)}
                                    >
                                        <span className={styles.warningDot} />
                                    </div>
                                </TooltipPortal>
                            )}
                            {service?.isAdded ? (
                                <ToggleButton
                                    onChange={handleToggle}
                                    isEnabled={isEnabled}
                                />
                            ) : (
                                <PillButton
                                    onClick={handleAdd}
                                    size="small"
                                    title="Add service to your account"
                                    disabled={isLoading}
                                >
                                    <Plus size={'.875rem'} />
                                    Add
                                </PillButton>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.contentWrapper}>
                    <p className={styles.description}>{service.description}</p>
                    <div className={styles.details}>
                        {service?.isAdded && isShowDelete && (
                            <button
                                onClick={handleRemove}
                                className={styles.removeButton}
                                title="Remove service and all its tools"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                        <button
                            onClick={onClick}
                            className={styles.detailsButton}
                            title="View service details"
                        >
                            Details
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
});

ServiceCard.displayName = 'ServiceCard';

export default memo(ServiceCard);
